import React, { FC, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TableContainer,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ZoomIn } from '@mui/icons-material';
import LogoSpinner from 'src/components/LogoSpinner';
import { DynamicTable, TableSearch } from 'src/components/DynamicTable/DynamicTable';
import {
  ReferralPayoutResponse,
  ReferralPayoutStatus
} from '../Referral/referral.interface';
import { useReferralPayout } from 'src/hooks/useReferralPayout';

const TABLE_TEXT_STYLE = {
  p: '2%',
  height: '100%',
  display: 'flex',
  alignItems: 'center'
};

interface ReferralPayoutContentProps {
  loading: boolean;
  page: number;
  rowsPerPage: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setRowsPerPage: React.Dispatch<React.SetStateAction<number>>;
  onRefresh?: () => void;
  payouts: ReferralPayoutResponse[] | undefined;
}

export const ReferralPayoutContent: FC<ReferralPayoutContentProps> = ({
  loading,
  page,
  rowsPerPage,
  payouts,
  onRefresh
}) => {
  const [filtered, setFiltered] = useState<ReferralPayoutResponse[]>(payouts || []);
  const [openDialog, setOpenDialog] = useState(false);
  const [selected, setSelected] = useState<ReferralPayoutResponse | null>(null);
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [statusValue, setStatusValue] = useState<ReferralPayoutStatus>('PENDING');

  const { loading: apiLoading, getReferralPayoutById, updateReferralPayoutStatus } = useReferralPayout();

  const statusChip = (status: ReferralPayoutStatus) => {
    const color =
      status === 'PAID' ? 'success' : status === 'PROCESSING' ? 'info' : status === 'FAILED' ? 'error' : 'warning';
    return <Chip title={status} label={status} color={color as any} size="small" />;
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatMoney = (amount?: number, currency?: string) => {
    if (amount === undefined || amount === null) return 'N/A';
    const sym = currency || 'NGN';
    return `${sym} ${Number(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const handleView = async (row: ReferralPayoutResponse) => {
    setOpenDialog(true);
    const full = await getReferralPayoutById(row.id);
    setSelected(full || row);
    setStatusValue((full || row).status);
  };

  const updateStatus = async (newStatus: ReferralPayoutStatus) => {
    if (!selected) return;
    const res = await updateReferralPayoutStatus(selected.id, { status: newStatus });
    if (res) {
      onRefresh?.();
      const full = await getReferralPayoutById(selected.id);
      setSelected(full || { ...selected, status: newStatus });
      setIsEditingStatus(false);
    }
  };

  const headers = useMemo(
    () => [
      { headerName: 'Referral Email', field: 'referrerEmail', minWidth: 140 },
      {
        headerName: 'Payout Amount',
        field: 'amount',
        minWidth: 160,
        renderCell: ({ row }) => <Box sx={TABLE_TEXT_STYLE}>{formatMoney(row.amount)}</Box>
      },
      { headerName: 'Payment Method',
        field: 'paymentMethod',
        minWidth: 100,
        renderCell: ({row}) => <Box sx={TABLE_TEXT_STYLE}>{row.paymentMethod.replaceAll("_"," ")?.toUpperCase()}</Box>
      },
      {
        headerName: 'Status',
        field: 'status',
        minWidth: 140,
        renderCell: ({ row }) => <Box sx={TABLE_TEXT_STYLE}>{statusChip(row.status)}</Box>
      },
      {
        headerName: 'Processed Requested',
        field: 'requestedAt',
        minWidth: 140,
        renderCell: ({ row }) => <Box sx={TABLE_TEXT_STYLE}>{formatDate(row.requestedAt || undefined)}</Box>
      },
      {
        headerName: 'Created',
        field: 'createdAt',
        minWidth: 140,
        renderCell: ({ row }) => <Box sx={TABLE_TEXT_STYLE}>{formatDate(row.createdAt)}</Box>
      },
      {
        headerName: 'Actions',
        field: 'actions',
        sortable: false,
        filterable: false,
        width: 140,
        renderCell: ({ row }) => (
          <Button size="small" startIcon={<ZoomIn />} onClick={() => handleView(row)}>
            View
          </Button>
        )
      }
    ],
    []
  );

  if (loading || apiLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignContent: 'center', py: '5%' }}>
        <LogoSpinner />
      </Box>
    );
  }

  return (
    <>
      <Card>
        <TableContainer>
          <DynamicTable
            showPagination={true}
            rowsPerPage={rowsPerPage}
            page={page}
            totalPage={payouts?.length ?? 0}
            searchElement={<TableSearch placeholder="Search Referral Payouts" searchData={[]} setSearchData={setFiltered} />}
            rows={(payouts || []).map((d) => ({
              ...d,
              rowHeight: 55
            }))}
            headers={headers}
          />
        </TableContainer>
      </Card>

      <Dialog fullWidth maxWidth="md" open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Typography variant="h4">Referral Payout Details</Typography>
            <IconButton color="error" onClick={() => setOpenDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selected ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2">Referral Email</Typography>
                <Typography variant="body1">{selected.referrerEmail}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">Payout Amount</Typography>
                <Typography variant="body1">{formatMoney(selected.amount, 'NGN')}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">Payment Method</Typography>
                <Typography variant="body1">{selected.paymentMethod.replace("_"," ").toUpperCase()}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">Status</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{statusChip(selected.status)}</Box>
              </Box>
              <Box>
                <Typography variant="subtitle2">Payout Status</Typography>
                <Typography variant="body1">{statusChip(selected.status)}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">Created</Typography>
                <Typography variant="body1">{formatDate(selected.createdAt)}</Typography>
              </Box>

              <Box sx={{ gridColumn: '1 / -1', mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Update Status
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <Select value={statusValue} onChange={(e) => setStatusValue(e.target.value as ReferralPayoutStatus)}>
                      {['PENDING', 'PROCESSING', 'PAID', 'FAILED'].map((s) => (
                        <MenuItem value={s} key={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button variant="outlined" onClick={() => setIsEditingStatus((v) => !v)}>
                    {isEditingStatus ? 'Cancel' : 'Edit Status'}
                  </Button>
                  <Button variant="contained" disabled={!isEditingStatus} onClick={() => updateStatus(statusValue)}>
                    Save
                  </Button>
                </Box>
              </Box>
            </Box>
          ) : (
            <Typography variant="body2">Loading...</Typography>
          )}
        </DialogContent>
        <DialogActions></DialogActions>
      </Dialog>
    </>
  );
};
